// ./src/components/features/Tasks/ResponsiblePartySelect/ResponsiblePartySelect.tsx
//
// Picks WHO is responsible for a task. The responsible party is polymorphic: a
// crew member or a guest (both event_guest rows) or a vendor or a venue (both
// venue rows). This control is a two-step picker — first the KIND
// (crew / guest / vendor / venue), then the specific party of that kind — and it
// lets the user create a new venue / vendor in place (mirrors VenuePickerField),
// so they never have to leave the task drawer.
//
// Emits (kind, id, label). The server re-derives the label snapshot, so the label
// we pass is only for optimistic display.
//
// Self-contained: it wraps its own VenuesProvider (all kinds) so VenueEditDrawer's
// in-place create lands in the same list, and loads the event's guests via
// useEventGuests — it works anywhere without a surrounding provider.

import React, { useMemo, useState } from "react";
import { Stack, Group, Text, Loader, Button, Box } from "@mantine/core";

import { VenuesProvider, useVenuesContext, useApp } from "@/contexts";
import { useEventGuests } from "@/hooks/useEventGuests";
import {
  Venue,
  VENUE_KIND,
  EVENT_GUEST_ROLE,
  TEAM_ROLES,
  getGuestDisplayName,
  getGuestRoleLabel,
  TASK_RESPONSIBLE_KIND,
  WHOLE_SET,
} from "@/utils/api";
import { AppIcon, SelectField } from "@/components/generic";
import { DRAWER_COMBOBOX_PROPS } from "@/utils";
import { VenueEditDrawer } from "@/components/features/Venues";
import { useCreateInPlace } from "@/hooks/useCreateInPlace";
import { CREATE_IN_OWN_ACCOUNT } from "@/utils/createInPlace";

const { USER, CREW, GUEST, VENDOR, VENUE } = TASK_RESPONSIBLE_KIND;

const KIND_META: Record<string, { label: string; icon: string }> = {
  [USER]: { label: "Me", icon: "UserCheck" },
  [CREW]: { label: "Crew member", icon: "Users" },
  [GUEST]: { label: "Guest", icon: "User" },
  [VENDOR]: { label: "Vendor", icon: "Briefcase" },
  [VENUE]: { label: "Venue", icon: "MapPin" },
};

/** Best display name for the signed-in account user. */
const userDisplayName = (u: { firstName?: string | null; lastName?: string | null; username?: string | null; email?: string | null } | null): string => {
  if (!u) return "Me";
  const name = [u.firstName, u.lastName].filter(Boolean).join(" ").trim();
  return name || u.username || u.email || "Me";
};

export interface ResponsiblePartyValue {
  kind?: string | null;
  id?: string | null;
}

export interface ResponsiblePartySelectProps {
  /** Scopes crew / guest options and any newly-created crew to this event. */
  eventId: string;
  value: ResponsiblePartyValue;
  onChange: (kind: string | null, id: string | null, label: string | null) => void;
  label?: string;
  description?: string;
  disabled?: boolean;
  /** A stale snapshot label to show when the referenced row is no longer in the loaded lists. */
  fallbackLabel?: string | null;
  /** Server refusal on the KIND field (e.g. `responsibleKind`, `paidByKind`). */
  kindError?: string | null;
  /** Server refusal on the ID field (e.g. `responsibleId`, `paidById`). */
  idError?: string | null;
}

// =============================================================================
// Inner — assumes a VenuesProvider ancestor (so VenueEditDrawer shares the list).
// =============================================================================

const ResponsiblePartySelectInner: React.FC<ResponsiblePartySelectProps> = ({
  eventId,
  value,
  onChange,
  label = "Responsible",
  description,
  disabled,
  fallbackLabel,
  kindError,
  idError,
}) => {
  const { venues, loading: venuesLoading } = useVenuesContext();
  const { guests, loading: guestsLoading, lastError: guestsError } = useEventGuests({
    initialFilters: { eventId: [eventId] },
    // A picker shows EVERY crew member / guest — read whole, never a capped page (ROADMAP #118).
    pageSize: WHOLE_SET,
  });
  const { user } = useApp();
  const me = user?.id ? { id: user.id, label: userDisplayName(user) } : null;

  const [createDrawerKind, setCreateDrawerKind] = useState<string | null>(null);

  // #142 — a venue / vendor lands in the caller's own account: never offered to a crew member.

  const canCreateVenue = useCreateInPlace(CREATE_IN_OWN_ACCOUNT);

  const kind = value.kind ?? null;
  const id = value.id ?? null;
  const isSelf = kind === USER && !!me && id === me.id;

  const places = useMemo(() => venues.filter((v) => v.kind !== VENUE_KIND.VENDOR && !!v.id), [venues]);
  const vendors = useMemo(() => venues.filter((v) => v.kind === VENUE_KIND.VENDOR && !!v.id), [venues]);

  const crew = useMemo(
    () => guests.filter((g) => !!g.id && TEAM_ROLES.includes(g.guestRole || EVENT_GUEST_ROLE.GUEST)),
    [guests]
  );
  const attendees = useMemo(
    () => guests.filter((g) => !!g.id && (g.guestRole || EVENT_GUEST_ROLE.GUEST) === EVENT_GUEST_ROLE.GUEST),
    [guests]
  );

  const partyLoading = kind === CREW || kind === GUEST ? guestsLoading : venuesLoading;
  // ⚠ A failed guest read is not "None found" — the crew and the guests are there, the list could not be read. Said on
  //   the field; a server refusal on the id still wins.
  const partyReadError =
    (kind === CREW || kind === GUEST) && guestsError ? "Couldn’t load the guest list — close and open this again" : null;

  // Options for the second (party) select, given the chosen kind.
  const partyData = useMemo(() => {
    if (kind === CREW) {
      return crew.map((g) => ({ value: g.id as string, label: `${getGuestDisplayName(g)} · ${getGuestRoleLabel(g.guestRole)}` }));
    }
    if (kind === GUEST) {
      return attendees.map((g) => ({ value: g.id as string, label: getGuestDisplayName(g) }));
    }
    if (kind === VENUE) {
      return places.map((v) => ({ value: v.id as string, label: v.name || "Untitled venue" }));
    }
    if (kind === VENDOR) {
      return vendors.map((v) => ({ value: v.id as string, label: v.name || "Untitled vendor" }));
    }
    return [];
  }, [kind, crew, attendees, places, vendors]);

  const labelFor = (partyKind: string, partyId: string): string | null => {
    if (partyKind === CREW || partyKind === GUEST) {
      const g = guests.find((x) => x.id === partyId);
      return g ? getGuestDisplayName(g) : null;
    }
    const v = venues.find((x) => x.id === partyId);
    return v?.name || null;
  };

  const assignMyself = () => {
    if (me) {
      onChange(USER, me.id, me.label);
    }
  };

  const handleKindChange = (nextKind: string | null) => {
    // "Me" resolves to the current user immediately; any other kind resets who.
    if (nextKind === USER && me) {
      assignMyself();
      return;
    }
    onChange(nextKind, null, null);
  };

  const handlePartyChange = (nextId: string | null) => {
    if (!kind || !nextId) {
      onChange(kind, null, null);
      return;
    }
    onChange(kind, nextId, labelFor(kind, nextId));
  };

  const handleVenueCreated = (venue: Venue) => {
    setCreateDrawerKind(null);
    if (venue.id) {
      onChange(kind, venue.id, venue.name || null);
    }
  };

  // If the selected id isn't in the loaded list yet, keep it selectable so the
  // field isn't blanked (e.g. a snapshot of a removed/absent row).
  const partyDataWithSelected = useMemo(() => {
    if (!id || partyData.some((o) => o.value === id)) return partyData;
    const snapshot = fallbackLabel || labelFor(kind || "", id) || "Selected";
    return [{ value: id, label: snapshot }, ...partyData];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, partyData, fallbackLabel, kind]);

  const kindData = [
    ...(me ? [{ value: USER, label: KIND_META[USER].label }] : []),
    CREW,
    GUEST,
    VENDOR,
    VENUE,
  ].map((entry) => (typeof entry === "string" ? { value: entry, label: KIND_META[entry].label } : entry));

  return (
    <Stack gap="xs">
      <SelectField
        label={label}
        description={description}
        placeholder="No one assigned"
        data={kindData}
        value={kind}
        onChange={(_n, v) => handleKindChange(v)}
        clearable
        disabled={disabled}
        leftSection={<AppIcon iconName={kind ? KIND_META[kind].icon : "UserQuestion"} size={16} />}
        comboboxProps={DRAWER_COMBOBOX_PROPS}
        size="md"
        error={kindError || undefined}
      />

      {/* "Assign myself" shortcut — one click makes the current user responsible. */}
      {me && !isSelf ? (
        <Group>
          <Button
            variant="light"
            size="md"
            leftSection={<AppIcon iconName="UserCheck" size={14} />}
            onClick={assignMyself}
            disabled={disabled}
          >
            Assign myself
          </Button>
        </Group>
      ) : null}

      {kind === USER ? (
        <Box
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 10px",
            borderRadius: "var(--mantine-radius-md)",
            border: "1px solid var(--mantine-color-default-border)",
          }}
        >
          <AppIcon iconName="UserCheck" size={16} />
          <Text fz="md" fw={500}>
            {isSelf ? `${me?.label ?? "You"} (you)` : fallbackLabel || "Assigned user"}
          </Text>
        </Box>
      ) : kind ? (
        <SelectField
          placeholder={partyLoading ? "Loading…" : `Pick a ${KIND_META[kind].label.toLowerCase()}`}
          data={partyDataWithSelected}
          value={id}
          onChange={(_n, v) => handlePartyChange(v)}
          // Entity picker: full-screen on phones so the list + create action are
          // comfortable to use, however few rows there happen to be.
          createLabel={
            canCreateVenue && (kind === VENUE || kind === VENDOR) ? `Add a new ${kind === VENDOR ? "vendor" : "venue"}` : undefined
          }
          onCreate={
            canCreateVenue && (kind === VENUE || kind === VENDOR)
              ? () => setCreateDrawerKind(kind === VENDOR ? VENUE_KIND.VENDOR : VENUE_KIND.VENUE)
              : undefined
          }
          searchable
          clearable
          disabled={disabled}
          nothingFoundMessage={partyReadError ?? "None found"}
          rightSection={partyLoading ? <Loader size="md" /> : undefined}
          comboboxProps={DRAWER_COMBOBOX_PROPS}
          size="md"
          error={idError || partyReadError || undefined}
        />
      ) : null}

      {/* The party picker isn't rendered in the "myself" branch, and there is no
          picker at all until a kind is chosen — so an id refusal would have
          nowhere to land. Show it here rather than let it vanish. */}
      {idError && (isSelf || !kind) ? (
        <Text c="red" fz="sm">
          {idError}
        </Text>
      ) : null}

      {kind !== USER && value.kind && value.id && !partyLoading && !partyData.some((o) => o.value === value.id) && fallbackLabel ? (
        <Group gap={6} c="dimmed">
          <AppIcon iconName="InfoCircle" size={14} />
          <Text fz="md">Currently: {fallbackLabel}</Text>
        </Group>
      ) : null}

      <VenueEditDrawer
        opened={createDrawerKind != null}
        onClose={() => setCreateDrawerKind(null)}
        kind={createDrawerKind || VENUE_KIND.VENUE}
        onCreated={handleVenueCreated}
      />
    </Stack>
  );
};

// =============================================================================
// Public — wraps the inner in its own VenuesProvider (all kinds).
// =============================================================================

export const ResponsiblePartySelect: React.FC<ResponsiblePartySelectProps> = (props) => {
  return (
    <VenuesProvider pageSize={WHOLE_SET}>
      <ResponsiblePartySelectInner {...props} />
    </VenuesProvider>
  );
};

export default ResponsiblePartySelect;
